'use client';

import { c as _c } from "react/compiler-runtime";
import type { FundingCustomerVerification } from '@morfi/onramp-sdk/react';
import type { ChallengeEvent, MoonPayWebProvider } from '@morfi/onramp-sdk/web';
import { useTranslations } from 'next-intl';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { logger } from '@/lib/telemetry/logger';
type Props = {
  provider: Pick<MoonPayWebProvider, 'setupChallenge'>;
  challenge: NonNullable<FundingCustomerVerification['challenge']>;
  onRefresh(): Promise<boolean>;
};
export function FundingCustomerChallenge(props) {
  const $ = _c(3);
  const t0 = `${props.challenge.url}:${props.challenge.expiresAt}`;
  let t1;
  if ($[0] !== props || $[1] !== t0) {
    t1 = <ChallengeConsent key={t0} {...props} />;
    $[0] = props;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function ChallengeConsent(props) {
  const $ = _c(14);
  const t = useTranslations("onramp");
  const [started, setStarted] = useState(false);
  if (started) {
    let t0;
    if ($[0] !== props) {
      t0 = <SecureChallenge {...props} />;
      $[0] = props;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  let t0;
  if ($[2] !== t) {
    t0 = t("kyc_additional_step");
    $[2] = t;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] !== t0) {
    t1 = <p className="text-body text-foreground-secondary">{t0}</p>;
    $[4] = t0;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setStarted(true);
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t) {
    t3 = t("continue_with_moonpay");
    $[7] = t;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t3) {
    t4 = <AnimatedButton onClick={t2}>{t3}</AnimatedButton>;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t1 || $[12] !== t4) {
    t5 = <div className="flex flex-col gap-3">{t1}{t4}</div>;
    $[11] = t1;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}
function SecureChallenge({
  provider,
  challenge,
  onRefresh
}: Props): ReactNode {
  const t = useTranslations('onramp');
  const container = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [paused, setPaused] = useState(false);
  const refresh = useRef(onRefresh);
  const alive = useRef(true);
  const completed = useRef(false);
  useEffect(() => {
    alive.current = true;
    return (): void => {
      alive.current = false;
    };
  }, []);
  useEffect(() => {
    refresh.current = onRefresh;
  }, [onRefresh]);
  useEffect(() => {
    const host = container.current;
    if (!host) return;
    const remaining = Date.parse(challenge.expiresAt) - Date.now();
    const expired = !Number.isFinite(remaining) || remaining <= 0;
    if (expired) {
      const timer = setTimeout((): void => failChallenge(setFailed), 0);
      return (): void => clearTimeout(timer);
    }
    const owned = document.createElement('div');
    owned.className = 'h-full w-full';
    host.append(owned);
    let active = true;
    let frame: {
      dispose(): void;
    } | null = null;
    function stop(): void {
      active = false;
      owned.remove();
      frame?.dispose();
      frame = null;
    }
    const timer_0 = setTimeout((): void => {
      if (active) {
        stop();
        failChallenge(setFailed);
      }
    }, Math.min(remaining, 2147483647));
    void provider.setupChallenge({
      container: owned,
      url: challenge.url,
      onEvent: (event: ChallengeEvent): void => {
        if (!active) return;
        if (event.kind === 'complete') {
          completed.current = true;
          stop();
          // Frame completion is a refresh hint, never proof that KYC is active.
          void refresh.current().then((refreshed): void => {
            if (alive.current && !refreshed) failChallenge(setFailed);
          }).catch((): void => {
            if (alive.current) failChallenge(setFailed);
          });
        } else if (event.kind === 'error' || event.kind === 'cancelled') {
          stop();
          if (event.kind === 'cancelled') setPaused(true);else failChallenge(setFailed);
        }
      }
    }).then(result => {
      if (result.ok) {
        if (active) frame = result.value;else result.value.dispose();
      } else if (active) {
        stop();
        failChallenge(setFailed);
      }
    }).catch((): void => {
      if (active) {
        logger.warn('onramp.customer_challenge_setup_failed', {
          attempt: attempt + 1
        });
        stop();
        failChallenge(setFailed);
      }
    });
    return (): void => {
      clearTimeout(timer_0);
      stop();
    };
  }, [provider, challenge.url, challenge.expiresAt, attempt]);
  async function retry(): Promise<void> {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const refreshed_0 = await onRefresh();
      if (alive.current && !refreshed_0) failChallenge(setFailed);
      if (alive.current && refreshed_0 && !completed.current) {
        setFailed(false);
        setPaused(false);
        setAttempt(value => value + 1);
      }
    } catch {
      logger.warn('onramp.customer_challenge_refresh_failed', {
        phase: 'retry'
      });
      if (alive.current) failChallenge(setFailed);
    }
    if (alive.current) setRefreshing(false);
  }
  return <div className="rr-block flex flex-col gap-3" data-private="true">
      <div className={paused ? 'hidden' : 'h-[28rem]'} ref={container} />
      {paused && <div className="flex flex-col gap-1.5">
          <h3 className="text-heading-md">{t('kyc_paused_title')}</h3>
          <p className="text-body text-foreground-secondary">{t('kyc_paused_description')}</p>
        </div>}
      {failed && <p role="alert">{t('kyc_challenge_retry')}</p>}
      <AnimatedButton variant="secondary" loading={refreshing} disabled={refreshing} onClick={(): void => {
      void retry();
    }}>
        {paused ? t('kyc_resume') : t('kyc_check_status')}
      </AnimatedButton>
    </div>;
}
function failChallenge(setFailed: (failed: boolean) => void): void {
  logger.warn('onramp.customer_challenge_failed');
  setFailed(true);
}